<template>
  <div class="auth-logs-wrapper">
    <div class="header-section">
      <div class="page-title">
        <h2>Журнал авторизаций</h2>
        <p class="subtitle">Отслеживание активности сотрудников в системе</p>
      </div>
      
      <div class="filters-card">
        <!-- ОБНОВЛЕНО: Выбор диапазона дат -->
        <div class="control-group">
          <label>Период дат</label>
          <div class="input-wrapper date-range-wrapper" style="display: flex; gap: 8px; align-items: center;">
            <input 
              type="date" 
              v-model="startDate" 
              class="ui-input date-input"
            >
            <span>&mdash;</span>
            <input 
              type="date" 
              v-model="endDate" 
              class="ui-input date-input"
            >
            <button 
              v-if="startDate || endDate" 
              @click="clearDates" 
              class="clear-btn" 
              title="Очистить даты"
              aria-label="Очистить"
              style="margin-left: 4px;"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        <div class="control-group">
          <label for="store-filter">Магазин</label>
          <select id="store-filter" v-model="storeFilter" class="ui-input store-select">
            <option value="">Все магазины</option>
            <option v-for="store in availableStores" :key="store" :value="store">
              {{ store }}
            </option>
          </select>
        </div>

        <div class="control-group">
          <label for="sort-select">Сортировка</label>
          <select id="sort-select" v-model="sortBy" class="ui-input">
            <option value="date_desc">Сначала новые</option>
            <option value="date_asc">Сначала старые</option>
            <option value="name_asc">По имени (А-Я)</option>
            <option value="name_desc">По имени (Я-А)</option>
          </select>
        </div>
      </div>
    </div>

    <div class="table-card">
      <table class="logs-table">
        <colgroup>
          <col style="width: 15%;">
          <col style="width: 22%;">
          <col style="width: 28%;">
          <col style="width: 15%;">
          <col style="width: 20%;">
        </colgroup>
        <thead>
          <tr>
            <th>Дата и время</th>
            <th>Сотрудник</th>
            <th>Магазин</th>
            <th>IP Адрес</th>
            <th>Устройство / Браузер</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="visibleLogs.length === 0">
            <td colspan="5" class="empty-state">
              <div class="empty-content">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <p>По вашему запросу ничего не найдено</p>
                <span class="empty-hint">Попробуйте изменить период дат или выбрать другой магазин</span>
              </div>
            </td>
          </tr>
          <tr v-for="log in visibleLogs" :key="log.id">
            <td class="date-cell">
              <div class="date-time">
                <span class="time">{{ formatTime(log.login_date) }}</span>
                <span class="date">{{ formatOnlyDate(log.login_date) }}</span>
              </div>
            </td>
            <td class="employee-cell">
              <span class="employee-name">{{ log.employee }}</span>
            </td>
            <td class="store-cell">
              <span v-if="log.store" class="store-badge" :title="log.store">{{ log.store }}</span>
              <span v-else class="text-muted">Не привязан</span>
            </td>
            <td>
              <span class="ip-badge">{{ log.ip_address }}</span>
            </td>
            <td class="ua-cell" :title="log.user_agent">
              <div class="device-badge" :class="isMobile(log.user_agent) ? 'mobile' : 'desktop'">
                <span class="icon">{{ isMobile(log.user_agent) ? '📱' : '💻' }}</span>
                <span class="ua-text">{{ formatUserAgent(log.user_agent) }}</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination-section" v-if="hasMore">
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: (visibleLogs.length / sortedLogs.length) * 100 + '%' }"></div>
      </div>
      <p class="showing-info">Показано {{ visibleLogs.length }} из {{ sortedLogs.length }} записей</p>
      <button class="load-more-btn" @click="loadMore">Загрузить еще</button>
    </div>

    <div class="statistics-wrapper" v-if="filteredLogs.length > 0">
      <div class="page-title" style="margin-top: 48px; margin-bottom: 24px;">
        <h2>Аналитика авторизаций</h2>
        <p class="subtitle">Статистика по текущей выборке</p>
      </div>

      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-icon users">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
          </div>
          <div class="stat-info">
            <span class="stat-label">Уникальных сотрудников</span>
            <span class="stat-value">{{ statsSummary.unique }}</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon logins">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path><polyline points="10 17 15 12 10 7"></polyline><line x1="15" y1="12" x2="3" y2="12"></line></svg>
          </div>
          <div class="stat-info">
            <span class="stat-label">Всего входов</span>
            <span class="stat-value">{{ statsSummary.total }}</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon stores">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
          </div>
          <div class="stat-info">
            <span class="stat-label">Самый активный магазин</span>
            <span class="stat-value store-val" :title="statsSummary.topStore">{{ statsSummary.topStore }}</span>
          </div>
        </div>
        <!-- ОБНОВЛЕНО: Карточка худшего магазина -->
        <div class="stat-card">
          <div class="stat-icon worst-store">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline><polyline points="17 18 23 18 23 12"></polyline></svg>
          </div>
          <div class="stat-info">
            <span class="stat-label">Худший магазин (заходов: {{ statsSummary.minCount }})</span>
            <span class="stat-value store-val" :title="statsSummary.worstStore">{{ statsSummary.worstStore }}</span>
          </div>
        </div>
      </div>

      <div class="charts-grid">
        <div class="chart-card">
          <h4>Активность по времени (часы)</h4>
          <div class="bar-chart-vertical">
            <div v-for="item in hourlyChart" :key="item.hour" class="bar-col" :title="`Входов: ${item.count}`">
              <div class="bar-fill-wrapper">
                <div class="bar-fill" :style="{ height: item.height }">
                  <span class="bar-tooltip" v-if="item.count > 0">{{ item.count }}</span>
                </div>
              </div>
              <span class="bar-label">{{ item.label }}</span>
            </div>
          </div>
        </div>

        <div class="chart-card">
          <h4>Топ-5 магазинов</h4>
          <div class="bar-chart-horizontal">
            <div class="empty-hint" v-if="storeChart.length === 0">Нет данных для отображения</div>
            <div v-for="item in storeChart" :key="item.name" class="bar-row">
              <div class="bar-label-hor" :title="item.name">{{ item.name }}</div>
              <div class="bar-track">
                <div class="bar-fill-hor" :style="{ width: item.width }">
                  <span class="bar-val">{{ item.count }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const addressEmployeeMapping = {
  'г. Минск ул. Ваупшасова, 10': ['Савко Александр', 'Михаил Воробей'],
  'г. Минск ул. Володько, 9': ['Шунейко Алесь Юрьевич', 'Верниковский Станислав'],
  'г. Минск ул. Машино-лей, 9А к.1': ['Патиюк Дмитрий Геннадьевич', 'Головко Иван Андреевич', 'Вайтулянец Александр Валерьевич', 'Помазенко Александр Сергеевич'],
  'г. Минск ул. Бельского, 14': ['Рябов Владислав Русланович', 'Рябцев Вячеслав', 'Сусько Юрий Валерьевич', 'Груша Павел Васильевич'],
  'г. Витебск пр-т Строителей д. 12 к. 1': ['Гончаров Андрей', 'Васильев Евгений Валерьевич', 'Яковлев Алексей Викторович'],
  'г. Гомель ул. Подпольщиков 18': ['Шевцов Константин Сергеевич', 'Пилявский Вадим Николаевич'],
  'г. Гомель ул. Б. Хмельницкого, 59': ['Трояновский Никита', 'Козлов Владислав Викторович'],
  'г. Новополоцк ул. Молодёжная 166А': ['Корсак Александр', 'Ханевич Дмитрий', 'Иванов Александр Владимирович', 'Тофоров Павел Васильевич'],
  'г. Полоцк ул. Строительная, 19': ['Корсак Александр', 'Ханевич Дмитрий', 'Иванов Александр Владимирович', 'Тофоров Павел Васильевич', 'Самусенок Игорь Иванович'],
  'г. Брест ул. б-р Космонавтов 66': ['Муха Андрей', 'Личко Станислав Александрович', 'Васильняк Александр', 'Стахурский Илья Святославович'],
  'г. Могилев ул. Сурганова 13,а': ['Шумский Юрий Михайлович', 'Коршунов Антон Николаевич'],
  'г. Барановичи ул. Комсомольская, 17': ['Лешик Виталий Сергеевич', 'Карачун Валерий Васильевич', 'Андрущенко Тарас Сергеевич'],
  'г. Гродно ул. Горького, 63': ['Шкут Денис', 'Черноусов Владислав Юрьевич', 'Хлистовский Александр Иосифович'],
  'г. Бобруйск ул. Орджоникидзе, 1': ['Кирюхин Даниил Юрьевич', 'Семенцов Дмитрий Михайлович', 'Федотов Павел Александрович']
};

const employeeToStore = {};
Object.keys(addressEmployeeMapping).forEach(store => {
  addressEmployeeMapping[store].forEach(employee => {
    employeeToStore[employee] = store;
  });
});

export default {
  name: 'AuthLogs',
  data() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const formattedToday = `${yyyy}-${mm}-${dd}`;

    return {
      rawLogs: [],
      // ОБНОВЛЕНО: Используем диапазон вместо одной даты
      startDate: formattedToday,
      endDate: formattedToday,
      storeFilter: '',
      sortBy: 'date_desc',
      itemsPerPage: 50,
      currentPage: 1,
      isLoading: false
    };
  },
  computed: {
    availableStores() {
      return Object.keys(addressEmployeeMapping);
    },
    logsWithStores() {
      return this.rawLogs.map(log => ({
        ...log,
        store: employeeToStore[log.employee] || ''
      }));
    },
    filteredLogs() {
      return this.logsWithStores.filter(log => {
        // ОБНОВЛЕНО: Фильтрация по диапазону дат
        let matchesDate = true;
        
        if (this.startDate || this.endDate) {
          const logDate = new Date(log.login_date).getTime();
          // Если startDate не указан, берем начало эпохи. Иначе начало дня.
          const start = this.startDate ? new Date(this.startDate).setHours(0, 0, 0, 0) : 0;
          // Если endDate не указан, берем бесконечность. Иначе конец дня.
          const end = this.endDate ? new Date(this.endDate).setHours(23, 59, 59, 999) : Infinity;
          
          matchesDate = logDate >= start && logDate <= end;
        }

        const matchesStore = this.storeFilter ? log.store === this.storeFilter : true;
        return matchesDate && matchesStore;
      });
    },
    sortedLogs() {
      let result = [...this.filteredLogs]; 
      result.sort((a, b) => {
        if (this.sortBy === 'date_desc') return new Date(b.login_date) - new Date(a.login_date);
        if (this.sortBy === 'date_asc') return new Date(a.login_date) - new Date(b.login_date);
        if (this.sortBy === 'name_asc') return a.employee.localeCompare(b.employee);
        if (this.sortBy === 'name_desc') return b.employee.localeCompare(a.employee);
        return 0;
      });
      return result;
    },
    visibleLogs() {
      const limit = this.currentPage * this.itemsPerPage;
      return this.sortedLogs.slice(0, limit);
    },
    hasMore() {
      return this.visibleLogs.length < this.sortedLogs.length;
    },
    
    statsSummary() {
      const logs = this.filteredLogs;
      if (!logs.length) return { total: 0, unique: 0, topStore: '-', worstStore: '-', minCount: 0 };
      
      const unique = new Set(logs.map(l => l.employee)).size;
      
      // Инициализируем нулями ВСЕ магазины, чтобы корректно отслеживать магазины с 0 заходов
      const storeCounts = {};
      this.availableStores.forEach(store => {
        storeCounts[store] = 0;
      });
      
      logs.forEach(l => {
        if (l.store && storeCounts[l.store] !== undefined) {
          storeCounts[l.store]++;
        } else if (l.store) {
          storeCounts[l.store] = (storeCounts[l.store] || 0) + 1;
        }
      });
      
      // ОБНОВЛЕНО: Подсчет лучшего и худшего магазина
      let topStore = '-';
      let worstStore = '-';
      let maxCount = -1;
      let minCount = Infinity;
      
      for (const [store, count] of Object.entries(storeCounts)) {
        if (count > maxCount) {
          maxCount = count;
          topStore = store;
        }
        if (count < minCount) {
          minCount = count;
          worstStore = store;
        }
      }
      
      return { total: logs.length, unique, topStore, worstStore, minCount };
    },
    hourlyChart() {
      const logs = this.filteredLogs;
      const hours = Array.from({ length: 24 }, (_, i) => ({ hour: i, count: 0 }));
      
      logs.forEach(l => {
        if (l.login_date) {
          const dateObj = new Date(l.login_date);
          if (!isNaN(dateObj)) {
            const hour = dateObj.getHours();
            hours[hour].count++;
          }
        }
      });
      
      const max = Math.max(...hours.map(h => h.count)) || 1;
      return hours.map(h => ({
        ...h,
        height: `${(h.count / max) * 100}%`,
        label: `${h.hour}`
      }));
    },
    storeChart() {
      const logs = this.filteredLogs;
      const storeCounts = {};
      
      logs.forEach(l => {
        const s = l.store || 'Не привязан';
        storeCounts[s] = (storeCounts[s] || 0) + 1;
      });
      
      const sorted = Object.entries(storeCounts)
        .map(([name, count]) => ({ name, count }))
        .filter(item => item.name !== 'Не привязан')
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
        
      const max = Math.max(...sorted.map(s => s.count)) || 1;
      
      return sorted.map(s => ({
        ...s,
        width: `${(s.count / max) * 100}%`
      }));
    }
  },
  watch: {
    startDate() { this.currentPage = 1; },
    endDate() { this.currentPage = 1; },
    storeFilter() { this.currentPage = 1; },
    sortBy() { this.currentPage = 1; }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.isLoading = true;
      try {
        const response = await fetch('https://1akb.by/brak/checkbot_brak/get_time_sellers.php');
        if (!response.ok) throw new Error('Ошибка сети');
        this.rawLogs = await response.json();
      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
      } finally {
        this.isLoading = false;
      }
    },
    clearDates() {
      this.startDate = '';
      this.endDate = '';
    },
    loadMore() {
      this.currentPage++;
    },
    formatTime(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    },
    formatOnlyDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    formatUserAgent(ua) {
      if (!ua) return 'Неизвестно';
      if (ua.length > 30) return ua.substring(0, 30);
      return ua;
    },
    isMobile(ua) {
      if (!ua) return false;
      return /Mobile|Android|iP(hone|od|ad)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/i.test(ua);
    }
  }
};
</script>

<style scoped>
/* Базовые стили обертки */
.auth-logs-wrapper {
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 24px;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: #0f172a;
  background-color: #f8fafc;
  min-height: 100vh;
}

/* Заголовок */
.header-section {
  margin-bottom: 32px;
}

.page-title {
  margin-bottom: 24px;
}

.page-title h2 {
  margin: 0;
  font-size: 32px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.03em;
}

.subtitle {
  margin: 8px 0 0;
  color: #64748b;
  font-size: 15px;
}

/* Карточка фильтров */
.filters-card {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  background-color: #ffffff;
  padding: 24px 32px;
  border-radius: 16px;
  box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.03), 0 0 3px rgba(15, 23, 42, 0.02);
  border: 1px solid #f1f5f9;
  align-items: flex-end;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 240px;
}

label {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.ui-input {
  width: 100%;
  padding: 12px 16px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  color: #1e293b;
  outline: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: inherit;
}

.ui-input:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
}

.ui-input:focus {
  background-color: #ffffff;
  border-color: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.date-input {
  padding-right: 40px;
}

input[type="date"]::-webkit-clear-button {
  display: none;
  -webkit-appearance: none;
}

.clear-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.clear-btn:hover {
  background-color: #fee2e2;
  color: #ef4444;
}

.store-select, #sort-select {
  cursor: pointer;
}

/* Карточка таблицы */
.table-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.03), 0 0 3px rgba(15, 23, 42, 0.02);
  border: 1px solid #f1f5f9;
  overflow: hidden;
  min-height: 400px; /* Предотвращает скачки высоты при пустом результате */
}

.logs-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  table-layout: fixed; /* Фиксирует ширину ячеек */
}

th {
  background-color: #ffffff;
  padding: 20px 24px;
  font-weight: 600;
  font-size: 13px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #f1f5f9;
}

td {
  padding: 16px 24px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis; /* Обрезает слишком длинный текст точками */
}

tbody tr {
  transition: background-color 0.2s ease;
}

tbody tr:hover {
  background-color: #f8fafc;
}

tbody tr:last-child td {
  border-bottom: none;
}

/* Пустое состояние */
.empty-state {
  padding: 80px 20px;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.empty-content p {
  margin: 12px 0 0;
  font-size: 16px;
  font-weight: 600;
  color: #334155;
}

.empty-hint {
  font-size: 14px;
  color: #94a3b8;
}

/* Контент ячеек */
.date-time {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.date-time .time {
  font-weight: 600;
  color: #1e293b;
  font-size: 15px;
}

.date-time .date {
  font-size: 13px;
  color: #64748b;
}

.employee-name {
  font-weight: 600;
  color: #0f172a;
}

.store-badge {
  display: inline-block;
  padding: 6px 12px;
  background-color: #f1f5f9;
  color: #334155;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text-muted {
  color: #94a3b8;
  font-size: 13px;
  font-style: italic;
}

.ip-badge {
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 13px;
  color: #475569;
  background: #f8fafc;
  padding: 4px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.device-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  max-width: 100%;
}

.device-badge.desktop {
  background-color: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.device-badge.mobile {
  background-color: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.ua-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Пагинация */
.pagination-section {
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.progress-bar {
  width: 200px;
  height: 4px;
  background-color: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #3b82f6;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.showing-info {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.load-more-btn {
  background-color: #ffffff;
  color: #0f172a;
  border: 1px solid #cbd5e1;
  padding: 12px 32px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.02);
}

.load-more-btn:hover {
  background-color: #f8fafc;
  border-color: #94a3b8;
  transform: translateY(-2px);
  box-shadow: 0 6px 12px -2px rgba(15, 23, 42, 0.05);
}

.load-more-btn:active {
  transform: translateY(0);
}

/* ==================================================
   НОВЫЕ СТИЛИ ДЛЯ АНАЛИТИКИ И ГРАФИКОВ
   ================================================== */
.statistics-wrapper {
  margin-top: 20px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
}

.stat-card {
  background: #ffffff;
  padding: 24px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 20px;
  box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.03), 0 0 3px rgba(15, 23, 42, 0.02);
  border: 1px solid #f1f5f9;
  transition: transform 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon.users { background: #eff6ff; color: #3b82f6; }
.stat-icon.logins { background: #f0fdf4; color: #22c55e; }
.stat-icon.stores { background: #fef2f2; color: #ef4444; }

.stat-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow: hidden;
}

.stat-label {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-value {
  font-size: 28px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1;
}

.stat-value.store-val {
  font-size: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Сетка графиков */
.charts-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
}

.chart-card {
  background: #ffffff;
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.03), 0 0 3px rgba(15, 23, 42, 0.02);
  border: 1px solid #f1f5f9;
}

.chart-card h4 {
  margin: 0 0 32px 0;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
}

/* Вертикальный Bar Chart (Часы) */
.bar-chart-vertical {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  height: 220px;
  gap: 4px;
  padding-bottom: 20px;
}

.bar-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  height: 100%;
}

.bar-fill-wrapper {
  width: 100%;
  height: 100%;
  background: #f1f5f9;
  border-radius: 6px;
  display: flex;
  align-items: flex-end;
  position: relative;
}

.bar-fill {
  width: 100%;
  background: #3b82f6;
  border-radius: 6px;
  transition: height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.bar-col:hover .bar-fill {
  background: #2563eb;
}

.bar-tooltip {
  position: absolute;
  top: -28px;
  left: 50%;
  transform: translateX(-50%);
  background: #1e293b;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
}

.bar-tooltip::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 4px 4px 0;
  border-style: solid;
  border-color: #1e293b transparent transparent transparent;
}

.bar-col:hover .bar-tooltip {
  opacity: 1;
}

.bar-label {
  margin-top: 8px;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

/* Горизонтальный Bar Chart (Топ магазинов) */
.bar-chart-horizontal {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.bar-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bar-label-hor {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bar-track {
  width: 100%;
  height: 24px;
  background: #f1f5f9;
  border-radius: 6px;
  overflow: hidden;
}

.bar-fill-hor {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 24px;
}

.bar-val {
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
}

/* Адаптивность для аналитики */
@media (max-width: 1024px) {
  .charts-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .filters-card {
    padding: 20px;
  }
  .control-group {
    min-width: 100%;
  }
  .bar-chart-vertical {
    gap: 2px;
  }
  .bar-label {
    font-size: 10px;
  }
}
</style>